import { runInAction } from 'mobx';
import { Observable, Subject, Subscription } from 'rxjs';
import { isNumber } from '@cutwise/utils';

import useStore from '@/common/hooks/useStore';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { AccessTargetType } from '@/security/types/AccessTarget';
import { DataAccess } from '@/security/types/DataAccess';

export interface EntityAccessChange {
  id: StringOrNumber;
  access: GlobalAccessLevel;
  dataAccess?: Nullable<DataAccess>;
}

export interface EntityAccessChangedEvent {
  targetType: AccessTargetType;
  changes: EntityAccessChange[];
}

export interface AccessAwareEntity {
  id: StringOrNumber;
  access: number;
  dataAccess?: Nullable<number>;
}

type EntitiesGetter = () => Nullable<AccessAwareEntity | AccessAwareEntity[]>;

export class EntityAccessEventEmitter {
  private eventSubject = new Subject<EntityAccessChangedEvent>();

  get event$(): Observable<EntityAccessChangedEvent> {
    return this.eventSubject.asObservable();
  }

  emitAccessChange(targetType: AccessTargetType, changes: EntityAccessChange[]) {
    if (changes.length === 0) {
      return;
    }

    this.eventSubject.next({ targetType, changes });
  }

  subscribeProducts(getProducts: EntitiesGetter): Subscription {
    return this.subscribeEntities('product', getProducts);
  }

  subscribeProjects(getProjects: EntitiesGetter): Subscription {
    return this.subscribeEntities('project', getProjects);
  }

  private subscribeEntities(targetType: AccessTargetType, getEntities: EntitiesGetter): Subscription {
    return this.event$.subscribe((event) => {
      if (event.targetType !== targetType) {
        return;
      }

      const entities = getEntities();

      if (!entities) {
        return;
      }

      const changesById = new Map(event.changes.map((change) => [`${change.id}`, change]));
      const entityList = Array.isArray(entities) ? entities : [entities];

      runInAction(() => {
        entityList.forEach((entity) => {
          const change = changesById.get(`${entity.id}`);

          if (!change) {
            return;
          }

          entity.access = change.access;

          if (isNumber(change.dataAccess)) {
            entity.dataAccess = change.dataAccess;
          }
        });
      });
    });
  }
}

export const useEntityAccessEventEmitter = () => useStore(EntityAccessEventEmitter);
